import { DateTime } from "luxon";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { Breadcrumb } from "../components/Breadcrumbs";
import { Comments } from "../components/Comments";
import { Flex } from "../components/Flex";
import { Loader } from "../components/Loader";
import { Paragraph } from "../components/Paragraph";
import Topbar from "../components/Topbar";
import { useIndividual } from "../data/useIndividual";
import { ROUTE } from "../routes";
import { buildName, capitalise } from "../utils/string";
import { Image } from "../components/Image";
import { Button } from "../components/Button";
import commentsIcon from "../assets/comments-icon.svg";
import { Card } from "../components/Card";
import { RecordSharing } from "../components/RecordAccess";
import { Box } from "../components/Box";
import axios from "axios";
import theme from "../theme";
import { showError } from "../utils/error-handling";
import { notify, clearAll } from "../utils/notify";
import { useDeleteIndividual } from "../data/useDeleteIndividual";
import { useAuth } from "../context/AuthContext";
import downloadIcon from "../assets/download-arrow.svg";
import { Modal } from "../components/Modal";
import { useState } from "react";
import { FaTrash } from "react-icons/fa";
import { IconButtonDropdown } from "../components/IconButtonDropdown";
import { IconButtonDropdownOption } from "../components/IconButtonDropdown/IconButtonDropdownOption";
import { env } from "../utils/env";
import resendInviteIcon from "../assets/resend-invite-icon.svg";
import {
  MandateStatus,
  useMandateStatuses,
} from "../context/MandateStatusContext";
import { useMandate } from "../data/useMandate";
import { ProfileRecordWidget } from "../components/ProfileRecordWidget";
import { SubjectSummaryCard } from "../components/SubjectSummaryCard";
import { CheckTabStrip } from "./IndividualView/CheckTabStrip";
import { CheckDetailPanel } from "./IndividualView/CheckDetailPanel";
import { useSelectedCheck } from "./IndividualView/useSelectedCheck";

const Container = styled.div`
  ${({ theme: { space } }) => `
    padding: ${space[4]};
    height: calc(100vh - 215px);
    overflow-y: auto;
    padding-bottom: ${space[5]}
  `}
`;

const formatDate = (value?: string | null) =>
  value ? DateTime.fromISO(value).toFormat("dd/MM/yyyy") : undefined;

const getRecordDisplayName = (record: any) => {
  const personName = [
    record?.first_name,
    record?.middle_name,
    record?.last_name,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    record?.company_name ||
    record?.profile?.name ||
    record?.name ||
    personName ||
    "-"
  );
};

/** A single comma-joined line, wrapping rather than truncating. */
const AddressValue = ({ address }: { address: any }) => {
  if (!address) return undefined;

  const parts = [
    address.flat_appartment_subbuilding,
    address.building_number,
    address.building_name,
    address.road_street,
    address.town_city,
    address.district,
    address.post_zip_code,
    address.country_name,
  ].filter(Boolean);

  // an inline img inside one Paragraph, not a sibling flex item: a flex row
  // wraps whole items, which pushed the flag onto its own line the moment the
  // address text needed more than one line to itself
  return (
    <Paragraph mt={0} mb={0} fontSize={1}>
      {address.country_flag && (
        <img
          src={`data:image/png;base64,${address.country_flag}`}
          alt=""
          style={{
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            verticalAlign: "middle",
            marginRight: "6px",
          }}
        />
      )}
      {parts.join(", ")}
    </Paragraph>
  );
};

export const IndividualChecksView = () => {
  const params = useParams();
  const { authHeader, hasPermission } = useAuth();
  const navigate = useNavigate();

  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [isRequestingPdfDownload, setIsRequestingPdfDownload] = useState(false);

  const { data: individualData, isLoading } = useIndividual(params.id);

  const individual = individualData?.data;

  const individualName = isLoading ? "..." : getRecordDisplayName(individual);

  // this route also renders company screening records (see getRecordDisplayName's
  // company_name fallback above) — company_name is only ever populated for those
  const isEntity = Boolean(individual?.company_name);

  const { getMandateStatusId } = useMandateStatuses();

  const { data: mandateData } = useMandate(individual?.mandate);
  const mandate = mandateData?.data;

  const { mutate: deleteIndividual } = useDeleteIndividual();

  const { selectedId, selectCheck } = useSelectedCheck(individual?.all_checks);
  const selectedCheck = individual?.all_checks?.find(
    (check: any) => check.id === selectedId
  );

  const canDelete =
    mandate?.status?.id !== getMandateStatusId(MandateStatus.Rejected);

  const handleDownloadPdfClick = () => {
    if (isRequestingPdfDownload) {
      return;
    }

    setIsRequestingPdfDownload(true);
    notify("Generating PDF, this could take up to 30 seconds", {
      position: "bottom-left",
      type: "info",
      pauseOnFocusLoss: false,
      pauseOnHover: false,
      autoClose: false,
    });

    axios
      .get(`${env.verifyServiceUrl}individual/download_pdf/${params.id}`, {
        headers: {
          Authorization: authHeader,
        },
        responseType: "arraybuffer",
        skipGlobalErrorHandler: true,
      })
      .then((response) => {
        clearAll();
        const blob = new Blob([response.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        const timestamp = DateTime.now().toFormat("yyyy-MM-dd_HH-mm-ss");

        const reportName = getRecordDisplayName(individual);

        link.download = `Customer Report - ${individual?.reference} - ${reportName} - ${timestamp}.pdf`;

        link.click();
      })
      .catch(() => {
        clearAll();
        setTimeout(() => {
          notify("There was a problem while generating the PDF report", {
            position: "bottom-left",
            type: "error",
          });
        }, 100);
      })
      .finally(() => {
        setIsRequestingPdfDownload(false);
      });
  };

  const handleConfirmDeleteIndividual = () => {
    setIsConfirmDeleteOpen(false);

    deleteIndividual(
      { id: params.id },
      {
        onSuccess: () => {
          navigate(`/screening`);
          notify("Individual removed", {
            position: "bottom-left",
            type: "success",
          });
        },
        onError: (err: any) => {
          showError(err, "A problem occurred while deleting the individual.");
        },
      }
    );
  };

  return (
    <div>
      <Topbar
        header={
          <Flex justifyContent="space-between" width="100%">
            <>
              <Breadcrumb>
                <Breadcrumb.Section linkTo={ROUTE.CHECKS}>
                  Screening
                </Breadcrumb.Section>

                <Breadcrumb.Section>{individualName}</Breadcrumb.Section>
              </Breadcrumb>
              <Flex justifyContent="space-between">
                {!individual?.company_name && (
                  <Button
                    onClick={() =>
                      navigate(`/applications/new`, {
                        state: { checks: true, id: individual?.mandate },
                      })
                    }
                    mr={1}
                    variant="gray"
                    paddingLeft={2}
                    paddingRight={2}
                    backgroundColor="gray.10"
                    border="2px solid background"
                    color="blue.primary"
                    fontWeight={600}
                    isDisabled={mandate?.checks_in_pending !== 0}
                  >
                    <Image
                      src={resendInviteIcon}
                      alt="check to application"
                      height="auto"
                      width="22px"
                      mr={1}
                    />
                    Convert to application
                  </Button>
                )}

                {/* A screening record is a "Checks" mandate — its Division/Team
                    tagging lives on, and is saved to, the mandate. */}
                <Box mr={1}>
                  <RecordSharing
                    module="screening"
                    recordId={mandate?.id}
                    recordName={individualName}
                    recordReference={individual?.reference}
                    noun="screening record"
                    primaryDivision={mandate?.primary_division}
                    primaryTeam={mandate?.primary_team}
                    additionalDivisions={mandate?.additional_divisions}
                    additionalTeams={mandate?.additional_teams}
                    invalidateKeys={[
                      ["mandate", mandate?.id],
                      ["individual", params.id],
                    ]}
                  />
                </Box>

                <Button
                  onClick={() => setIsCommentsOpen(!isCommentsOpen)}
                  mr={1}
                  variant="secondary"
                >
                  <Image
                    src={commentsIcon}
                    alt="comments"
                    height="auto"
                    width="22px"
                    mr={1}
                  />
                  Comments
                </Button>

                {individual?.can_download_pdf && (
                  <Button
                    onClick={handleDownloadPdfClick}
                    isDisabled={
                      isRequestingPdfDownload ||
                      !hasPermission("screening.downloadpdf")
                    }
                    title={
                      hasPermission("screening.downloadpdf")
                        ? undefined
                        : "You don't have permission to download this check as a PDF"
                    }
                    variant="secondary"
                    color={theme.colors.gray[80]}
                    mr={1}
                  >
                    <Image
                      src={downloadIcon}
                      alt="download pdf"
                      height="auto"
                      width="22px"
                      mr={1}
                    />
                    <Paragraph fontWeight="500" fontSize={theme.fontSizes[1]}>
                      {isRequestingPdfDownload
                        ? "Downloading..."
                        : "Download PDF"}
                    </Paragraph>
                  </Button>
                )}

                {canDelete && (
                  <IconButtonDropdown placement="bottom-end">
                    <IconButtonDropdownOption
                      onClick={() => setIsConfirmDeleteOpen(true)}
                      IconType={FaTrash}
                      isDestructive
                    >
                      Delete Individual
                    </IconButtonDropdownOption>
                  </IconButtonDropdown>
                )}
              </Flex>
            </>
          </Flex>
        }
      ></Topbar>

      {isLoading && (
        <Flex justifyContent="center" alignItems="center" minHeight="60vh">
          <Loader />
        </Flex>
      )}

      {!isLoading && (
        <>
          <Container>
            <Flex gap="24" alignItems="stretch" mb={4} flexWrap="wrap">
              <SubjectSummaryCard
                name={
                  isEntity
                    ? individual?.company_name
                    : buildName([
                        individual?.first_name,
                        individual?.middle_name,
                        individual?.last_name,
                      ])
                }
                chipLabel={isEntity ? "Entity" : "Individual"}
                identityFacts={
                  isEntity
                    ? [
                        individual?.entity_type?.name,
                        individual?.registration_number
                          ? `${individual.registration_number}`
                          : null,
                        individual?.date_of_incorporation
                          ? `Incorporated ${formatDate(
                              individual.date_of_incorporation,
                            )}`
                          : null,
                      ]
                    : [
                        individual?.date_of_birth
                          ? `Born ${formatDate(individual.date_of_birth)}`
                          : "No date of birth",
                        individual?.gender
                          ? capitalise(individual.gender)
                          : null,
                      ]
                }
                fields={[
                  { label: "Reference", value: individual?.reference },
                  {
                    label: "Your reference",
                    value: individual?.external_reference,
                  },
                  {
                    label: "Address",
                    value: <AddressValue address={individual?.address} />,
                  },
                  {
                    label: "Screening completed",
                    value: formatDate(individual?.checks_completed_date),
                  },
                  {
                    label: "Delete date",
                    value: formatDate(individual?.delete_date),
                    tooltip:
                      "This screening record will be deleted automatically on this date as part of our data retention policy.",
                  },
                ]}
              />

              <Card mb={0} p={3} width="300px" flexShrink={0}>
                <ProfileRecordWidget
                  record={individual}
                  recordId={individual?.id}
                  recordType="screening"
                  subjectType="individual"
                />
              </Card>
            </Flex>

            <CheckTabStrip
              checks={individual?.all_checks ?? []}
              selectedId={selectedId}
              onSelect={selectCheck}
            />

            <CheckDetailPanel check={selectedCheck} type="individual" />
          </Container>
          <Comments
            isOpen={isCommentsOpen}
            onRequestClose={() => setIsCommentsOpen(false)}
            referenceId={individual?.id}
            commentType="Individual"
          />
          <Modal
            isOpen={isConfirmDeleteOpen}
            onClose={() => setIsConfirmDeleteOpen(false)}
            onModalConfirmClick={handleConfirmDeleteIndividual}
            title="Do you want to delete individual?"
            content="This is an irreversible action, if you delete it you will not be able to undo the change. "
            hasSecondaryButton
            secondaryButtonText="Cancel"
            width="450px"
            primaryButtonText="Delete"
            primaryButtonVariant="danger"
          />
        </>
      )}
    </div>
  );
};
